<template>
    <AppForm
        v-if="loaded"
        :loading="loading"
        :is-new="isNew"
        heading="Products"
        @submit="submitHandler"
    >
        <template v-slot:tabs>
            <Tabs
                :tabs="tabs"
                :active="activeTab"
                @changed="value => (activeTab = value)"
            />
        </template>
        <template v-slot:main>
            <div v-show="activeTab === 'general'">
                <FormGroup label="Article number" label-for="article_number">
                    <InputField
                        v-model="product.article_number"
                        :required="true"
                        name="article_number"
                        type="text"
                    />
                </FormGroup>

                <FormGroup label="Gallery">
                    <MultipleMediaField
                        :initial-items="product.gallery"
                        type="image"
                        @changed="media => (product.gallery = media)"
                    />
                </FormGroup>

                <FormGroup
                    v-for="locale in locales"
                    :key="`title_${locale}`"
                    :flag="locale"
                    :label-for="`title_${locale}`"
                    label="Title"
                >
                    <InputField
                        v-model="product[locale].title"
                        :required="true"
                        :name="`title_${locale}`"
                        type="text"
                    />
                </FormGroup>

                <FormGroup
                    v-for="locale in locales"
                    :key="`description_${locale}`"
                    :flag="locale"
                    label="Description"
                >
                    <WYSIWYG v-model="product[locale].description" />
                </FormGroup>
            </div>

            <div v-show="activeTab === 'variants'">
                <FormGroup label="Product variants">
                    <Repeater
                        :rows="product.product_variants"
                        @removeRow="
                            index => product.product_variants.splice(index, 1)
                        "
                        @addRow="addVariants"
                    >
                        <template v-slot="{ row }">
                            <FormGroup
                                v-for="locale in locales"
                                :key="`title_${locale}`"
                                :flag="locale"
                                :label-for="`${row.id}_title_${locale}`"
                                label="Title"
                            >
                                <InputField
                                    v-model="row[locale].title"
                                    :name="`${row.id}_title_${locale}`"
                                    :required="true"
                                    type="text"
                                />
                            </FormGroup>
                            <FormGroup
                                :label-for="`${row.id}_article_number`"
                                label="Article number"
                            >
                                <InputField
                                    v-model="row.article_number"
                                    :name="`${row.id}_article_number`"
                                    :required="true"
                                    type="text"
                                />
                            </FormGroup>
                            <FormGroup label="Gallery">
                                <MultipleMediaField
                                    :initial-items="row.gallery"
                                    type="image"
                                    @changed="media => (row.gallery = media)"
                                />
                            </FormGroup>
                        </template>
                    </Repeater>
                </FormGroup>
            </div>

            <div v-show="activeTab === 'details'">
                <FormGroup
                    v-for="locale in locales"
                    :key="`detailed_information_${locale}`"
                    :flag="locale"
                    label="Detailed information"
                >
                    <WYSIWYG v-model="product[locale].detailed_information" />
                </FormGroup>

                <FormGroup label="Detailed information image">
                    <MediaField
                        :initial-media="product.detailed_information_image"
                        type="image"
                        @changed="
                            media =>
                                (product.detailed_information_image_id =
                                    media.id)
                        "
                    />
                </FormGroup>
            </div>

            <div v-show="activeTab === 'data'">
                <FormGroup label="Technical data">
                    <Repeater
                        :rows="product.technical_data_rows"
                        @removeRow="
                            index =>
                                product.technical_data_rows.splice(index, 1)
                        "
                        @addRow="addTechnicalData"
                    >
                        <template v-slot="{ row }">
                            <div
                                v-for="locale in locales"
                                :key="`technical_data_row_${row.id}_${locale}`"
                                class="flex"
                            >
                                <FormGroup
                                    :flag="locale"
                                    :label-for="`${row.id}_label_${locale}`"
                                    label="Label"
                                    class="mr-4 flex-grow"
                                >
                                    <InputField
                                        v-model="row[locale].label"
                                        :name="`${row.id}_label_${locale}`"
                                        :required="true"
                                        type="text"
                                    />
                                </FormGroup>

                                <FormGroup
                                    :label-for="`${row.id}_value_${locale}`"
                                    label="Value"
                                    class="flex-grow"
                                >
                                    <InputField
                                        v-model="row[locale].value"
                                        :name="`${row.id}_value_${locale}`"
                                        :required="true"
                                        type="text"
                                    />
                                </FormGroup>
                            </div>
                        </template>
                    </Repeater>
                </FormGroup>
            </div>

            <div v-show="activeTab === 'documents'">
                <FormGroup label="Documents">
                    <Relationship
                        :active-items="product.documents"
                        endpoint="/api/documents/all"
                        @addItem="item => product.documents.push(item.id)"
                        @removeItem="
                            index => product.documents.splice(index, 1)
                        "
                    />
                </FormGroup>
            </div>

            <div v-show="activeTab === 'other'">
                <FormGroup label="Other">
                    <Repeater
                        :rows="product.other"
                        @removeRow="index => product.other.splice(index, 1)"
                        @addRow="addOtherRow"
                    >
                        <template v-slot="{ row }">
                            <FormGroup
                                v-for="locale in locales"
                                :key="`title_${locale}`"
                                :flag="locale"
                                :label-for="`${row.id}_other_title_${locale}`"
                                label="Title"
                            >
                                <InputField
                                    v-model="row[locale].title"
                                    :name="`${row.id}_other_title_${locale}`"
                                    :required="true"
                                    type="text"
                                />
                            </FormGroup>
                            <FormGroup
                                :label-for="`${row.id}_other_url`"
                                label="Url"
                            >
                                <InputField
                                    v-model="row.url"
                                    :name="`${row.id}_other_url`"
                                    :required="true"
                                    type="text"
                                />
                            </FormGroup>
                            <FormGroup label="Type">
                                <AppSelect
                                    v-model="row.type"
                                    :items="[
                                        { label: 'Chose type', value: null },
                                        { label: 'Demo', value: 'demo' },
                                        { label: 'Link', value: 'link' },
                                        { label: 'Video', value: 'video' }
                                    ]"
                                />
                            </FormGroup>
                        </template>
                    </Repeater>
                </FormGroup>
            </div>

            <div v-show="activeTab === 'related'">
                <FormGroup label="Related products">
                    <Relationship
                        :active-items="product.related_products"
                        :exclude-id="!isNew ? parseInt($route.params.id) : null"
                        :max="4"
                        :short-article-number="true"
                        endpoint="/api/products/all"
                        @addItem="
                            item => product.related_products.push(item.id)
                        "
                        @removeItem="
                            index => product.related_products.splice(index, 1)
                        "
                    />
                </FormGroup>
            </div>
        </template>
        <template v-slot:side>
            <div v-if="!isNew" class="mb-8">
                <div class="timestamps mb-6">
                    <p>Created: {{ product.created_at }}</p>
                    <p>Updated: {{ product.updated_at }}</p>
                </div>
                <FormGroup
                    v-for="locale in locales"
                    :key="`slug_${locale}`"
                    :flag="locale"
                    :label-for="`slug_${locale}`"
                    label="Slug"
                >
                    <InputField
                        v-model="product[locale].slug"
                        :required="true"
                        :name="`slug_${locale}`"
                        type="text"
                    />
                </FormGroup>
            </div>

            <FormGroup label="Categories">
                <TaxonomyList
                    :initial-active="
                        product.categories ? product.categories : []
                    "
                    endpoint="/api/product-categories"
                    @changed="actives => (product.categories = actives)"
                />
            </FormGroup>
        </template>
    </AppForm>
</template>

<script>
import AppForm from "../../components/form/AppForm.vue";
import Tabs from "../../components/ui/Tabs.vue";
import FormGroup from "../../components/form/FormGroup.vue";
import InputField from "../../components/form/InputField.vue";
import WYSIWYG from "../../components/form/WYSIWYG.vue";
import TaxonomyList from "../../components/taxonomy-list/TaxonomyList.vue";
import Relationship from "../../components/form/Relationship.vue";
import AppSelect from "../../components/form/AppSelect.vue";
import Repeater from "../../components/repeater/Repeater.vue";
import MultipleMediaField from "../../components/form/MultipleMediaField.vue";
import { get, store, update } from "../../utils/apiHelper";
import { setupTranslation, locales } from "../../utils/translate";
import MediaField from "../../components/form/MediaField.vue";

export default {
    components: {
        AppForm,
        Tabs,
        FormGroup,
        InputField,
        WYSIWYG,
        TaxonomyList,
        Relationship,
        Repeater,
        AppSelect,
        MultipleMediaField,
        MediaField
    },
    props: {
        isNew: {
            type: Boolean,
            default: true
        }
    },
    data: () => ({
        loaded: false,
        loading: false,
        activeTab: "general",
        product: {
            title: null,
            article_number: null,
            gallery: null,
            content: "",
            detailed_information_image: null,
            detailed_information: "",
            technical_data_rows: [],
            categories: [],
            product_variants: [],
            documents: [],
            other: [],
            related_products: []
        },
        tabs: [
            {
                value: "general",
                label: "General"
            },
            {
                value: "variants",
                label: "Variants"
            },
            {
                value: "details",
                label: "Details"
            },
            {
                value: "data",
                label: "Data"
            },
            {
                value: "documents",
                label: "Documents"
            },
            {
                value: "other",
                label: "Other"
            },
            {
                value: "related",
                label: "Related"
            }
        ],
        locales
    }),
    async created() {
        if (!this.isNew) {
            const product = await get(`/api/products/${this.$route.params.id}`);
            this.setupForm(product);
        } else {
            this.product = {
                ...this.product,
                ...setupTranslation(
                    this.locales.map(locale => ({
                        locale,
                        title: null,
                        description: ""
                    }))
                )
            };
        }

        this.loaded = true;
    },
    methods: {
        setupForm(data) {
            const product = data;
            product.description = product.description
                ? product.description
                : "";
            product.other = product.other ? product.other : [];

            product.technical_data_rows = product.technical_data_rows
                ? product.technical_data_rows
                : [];

            product.product_variants = product.product_variants
                ? product.product_variants
                : [];

            if (product.product_variants.length > 0) {
                this.setupProductVariants(product.product_variants);
            }

            this.product = {
                ...product,
                ...setupTranslation(product.translations)
            };
        },
        addTechnicalData(row) {
            this.product.technical_data_rows.push({
                id: row.id,
                ...setupTranslation(
                    this.locales.map(locale => ({
                        locale,
                        label: null,
                        value: null
                    }))
                )
            });
        },
        addOtherRow(row) {
            this.product.other.push({
                id: row.id,
                ...setupTranslation(
                    this.locales.map(locale => ({
                        locale,
                        title: null
                    }))
                )
            });
        },
        setupProductVariants(productVariants) {
            const variants = productVariants;

            for (let i = 0; i < variants.length; i++) {
                variants[i] = {
                    ...variants[i],
                    ...setupTranslation(variants[i].translations)
                };
            }

            this.product.product_variants = variants;
        },
        addVariants(row) {
            this.product.product_variants.push({
                id: row.id,
                ...setupTranslation(
                    this.locales.map(locale => ({
                        locale,
                        title: null
                    }))
                )
            });
        },
        async submitHandler() {
            this.loading = true;

            if (this.isNew) {
                await this.create();
            } else {
                await this.update();
            }

            this.loading = false;
        },
        async create() {
            const res = await store("/api/products", this.product);

            if (res) {
                this.$router.push("/products");
            }
        },
        async update() {
            const res = await update(
                `/api/products/${this.$route.params.id}`,
                this.product
            );

            if (res) {
                this.setupForm(res.data.product);
            }
        }
    }
};
</script>
